import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CountryContext = createContext();

export const useCountry = () => {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
};

export const CountryProvider = ({ children }) => {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    // Load saved country from localStorage
    const savedCountryCode = localStorage.getItem('selected_country_code');
    if (savedCountryCode && countries.length > 0) {
      const country = countries.find(c => c.code === savedCountryCode);
      if (country) {
        setSelectedCountry(country);
      }
    } else if (countries.length > 0 && !selectedCountry) {
      // Default to first country (or USD if available)
      const usdCountry = countries.find(c => c.currency === 'USD');
      setSelectedCountry(usdCountry || countries[0]);
    }
  }, [countries]);

  const fetchCountries = async () => {
    try {
      const response = await apiRequest(`${BACKEND_URL}/api/admin/public/countries`, {
        method: 'GET',
      });

      if (response.ok) {
        setCountries(response.data.countries || []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setLoading(false);
    }
  };

  const selectCountry = (country) => {
    setSelectedCountry(country);
    localStorage.setItem('selected_country_code', country.code);
  };

  // Convert price from USD to selected country's currency
  const convertPrice = (priceInUSD) => {
    if (!selectedCountry || !priceInUSD) return priceInUSD;
    const rate = selectedCountry.conversion_rate_to_dollar || 1;
    return (priceInUSD * rate).toFixed(2);
  };

  // Format price with currency symbol
  const formatPrice = (priceInUSD) => {
    if (!selectedCountry) return `$${priceInUSD}`;
    const convertedPrice = convertPrice(priceInUSD);
    const currency = selectedCountry.currency || 'USD';
    
    // Common currency symbols
    const currencySymbols = {
      'USD': '$',
      'INR': '₹',
      'EUR': '€',
      'GBP': '£',
      'AUD': 'A$',
      'CAD': 'C$',
      'JPY': '¥',
      'CNY': '¥',
      'AED': 'د.إ',
      'SAR': '﷼',
    };
    
    const symbol = currencySymbols[currency] || currency + ' ';
    return `${symbol}${convertedPrice}`;
  };

  return (
    <CountryContext.Provider value={{
      countries,
      selectedCountry,
      selectCountry,
      convertPrice,
      formatPrice,
      loading,
    }}>
      {children}
    </CountryContext.Provider>
  );
};
